// Copyright (c) Docugami, Inc. All rights reserved.

import { useEffect, useMemo, useState } from 'react';
import { DocsetDocument, GetDocsetDocumentsRequest, GetDocumentsRequestFilters } from '../store/frontend/frontendSlice';
import { useVirtualizedDocuments } from "./VirtualizedDocuments";

interface IVirtualizedDocsetDocuments {
    isLoading: boolean;
    items: (DocsetDocument | null)[];
    totalCount?: number;
}

export function useVirtualizedDocsetDocuments(docSetId: string, pageSize: number, filters: GetDocumentsRequestFilters, pageOffsets: number[]): IVirtualizedDocsetDocuments {
    const [args, setArgs] = useState<GetDocsetDocumentsRequest | undefined>();
    const { isLoading, data, totalCount } = useVirtualizedDocuments(args, pageOffsets);

    const items = useMemo(() => {
        if (!data || !totalCount) {
            return [];
        }

        let result: (DocsetDocument | null)[] = Array(totalCount).fill(null);
        for (let offset = 0; offset < totalCount; offset += pageSize) {
            const page = data[offset];
            if (!!page) {
                page.forEach((doc, i) => {
                    result[offset + i] = doc;
                });
            }
        }

        return result;
    }, [data, totalCount, pageSize]);

    // Update request args and reset state on change
    useEffect(() => {
        setArgs({ ...filters, docsetId: docSetId, limit: pageSize });
    }, [docSetId, pageSize, filters])

    return {
        isLoading: isLoading,
        totalCount: totalCount,
        items: items
    }
}
